import React from 'react';
import { useSocialMediaPosts } from '@/hooks/useSocialMediaPosts';
import { Heart, MessageCircle, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const SocialMediaFeed: React.FC = () => {
  const { posts, isLoading, error } = useSocialMediaPosts();

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-card p-4 rounded-lg animate-pulse">
            <div className="h-4 bg-muted rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-muted rounded w-3/4 mb-3"></div>
            <div className="h-32 bg-muted rounded mb-3"></div>
            <div className="flex gap-4">
              <div className="h-4 bg-muted rounded w-16"></div>
              <div className="h-4 bg-muted rounded w-16"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Failed to load social media posts</p>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No social media posts available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {posts.map((post) => (
        <div key={post.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
          {/* Platform Badge */}
          <div className="flex items-center justify-between mb-3">
            <div className={`px-2 py-1 rounded text-xs font-medium ${
              post.platform === 'instagram' 
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                : 'bg-foreground text-background'
            }`}>
              {post.platform === 'instagram' ? 'Instagram' : 'X'}
            </div>
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(post.posted_at), { addSuffix: true })}
            </span>
          </div>

          {/* Content */}
          <p className="text-sm text-foreground mb-3 leading-relaxed">{post.content}</p>

          {/* Media */}
          {post.media_url && post.media_type === 'image' && (
            <div className="mb-3 rounded-lg overflow-hidden">
              <img 
                src={post.media_url} 
                alt="Social media post" 
                className="w-full h-48 object-cover"
                loading="lazy"
              />
            </div>
          )}

          {/* Engagement Stats */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Heart size={14} />
                <span>{post.likes_count}</span>
              </div>
              <div className="flex items-center gap-1">
                <MessageCircle size={14} />
                <span>{post.comments_count}</span>
              </div>
            </div>
            
            {post.post_url && (
              <a 
                href={post.post_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-primary transition-colors"
              >
                <ExternalLink size={14} />
                <span>View Post</span>
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SocialMediaFeed;